<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Digite a mensagem da campanha' },
  disabled: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const textareaRef = ref(null);

const value = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

// Wrap selected text with WhatsApp markdown markers
function wrapSelection(prefix, suffix = prefix) {
  const el = textareaRef.value;
  if (!el || props.disabled) return;
  el.focus();

  const start = el.selectionStart;
  const end = el.selectionEnd;
  const selected = el.value.slice(start, end);
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);

  // Toggle: if already wrapped, remove markers
  const isWrapped =
    el.value.slice(start - prefix.length, start) === prefix &&
    el.value.slice(end, end + suffix.length) === suffix;

  let newValue;
  let newStart;
  let newEnd;

  if (isWrapped) {
    newValue =
      el.value.slice(0, start - prefix.length) +
      selected +
      el.value.slice(end + suffix.length);
    newStart = start - prefix.length;
    newEnd = end - prefix.length;
  } else {
    newValue = before + prefix + selected + suffix + after;
    newStart = start + prefix.length;
    newEnd = end + prefix.length;
  }

  value.value = newValue;

  // Restore selection after Vue re-renders
  requestAnimationFrame(() => {
    el.setSelectionRange(newStart, newEnd);
  });
}

// Insert a bullet list or numbered list starter
function insertList(ordered = false) {
  const el = textareaRef.value;
  if (!el || props.disabled) return;
  el.focus();

  const start = el.selectionStart;
  const before = el.value.slice(0, start);
  const after = el.value.slice(start);

  // Find start of current line
  const lineStart = before.lastIndexOf('\n') + 1;
  const currentLine = before.slice(lineStart);
  const marker = ordered ? '1. ' : '- ';

  let newValue;
  let cursor;

  if (currentLine.startsWith(marker)) {
    // Remove marker if already present
    newValue =
      before.slice(0, lineStart) + currentLine.slice(marker.length) + after;
    cursor = start - marker.length;
  } else if (currentLine === '') {
    newValue = before + marker + after;
    cursor = start + marker.length;
  } else {
    newValue = before.slice(0, lineStart) + marker + currentLine + after;
    cursor = start + marker.length;
  }

  value.value = newValue;
  requestAnimationFrame(() => {
    el.setSelectionRange(cursor, cursor);
  });
}

// Undo/redo via execCommand (native)
function undo() {
  textareaRef.value?.focus();
  document.execCommand('undo');
}
function redo() {
  textareaRef.value?.focus();
  document.execCommand('redo');
}

const charCount = computed(() => props.modelValue.length);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div
    class="flex flex-col rounded-lg border overflow-hidden transition-colors"
    :class="[
      hasError
        ? 'border-n-red-8'
        : 'border-n-weak focus-within:border-n-slate-6',
      disabled ? 'opacity-60' : '',
    ]"
  >
    <!-- Toolbar -->
    <div
      class="flex items-center gap-0.5 px-2 py-1.5 border-b border-n-brand/30 bg-n-brand flex-wrap"
    >
      <!-- Bold → *text* -->
      <button
        type="button"
        title="Negrito (*texto*)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="wrapSelection('*')"
      >
        <span class="font-bold text-sm leading-none">B</span>
      </button>

      <!-- Italic → _text_ -->
      <button
        type="button"
        title="Itálico (_texto_)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="wrapSelection('_')"
      >
        <span class="italic text-sm leading-none">I</span>
      </button>

      <!-- Strikethrough → ~text~ -->
      <button
        type="button"
        title="Tachado (~texto~)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="wrapSelection('~')"
      >
        <span class="line-through text-sm leading-none">S</span>
      </button>

      <!-- Code → ```text``` -->
      <button
        type="button"
        title="Código (```texto```)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-n-slate-10 hover:bg-n-alpha-3 hover:text-n-slate-12 transition-colors disabled:opacity-30 disabled:cursor-not-allowed font-mono"
        @mousedown.prevent="wrapSelection('```')"
      >
        <span class="text-xs leading-none">&lt;&gt;</span>
      </button>

      <div class="w-px h-4 bg-white/25 mx-1" />

      <!-- Bullet list -->
      <button
        type="button"
        title="Lista com marcadores"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="insertList(false)"
      >
        <span class="i-lucide-list w-3.5 h-3.5" />
      </button>

      <!-- Numbered list -->
      <button
        type="button"
        title="Lista numerada"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="insertList(true)"
      >
        <span class="i-lucide-list-ordered w-3.5 h-3.5" />
      </button>

      <div class="w-px h-4 bg-white/25 mx-1" />

      <!-- Undo -->
      <button
        type="button"
        title="Desfazer (Ctrl+Z)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="undo"
      >
        <span class="i-lucide-undo-2 w-3.5 h-3.5" />
      </button>

      <!-- Redo -->
      <button
        type="button"
        title="Refazer (Ctrl+Y)"
        :disabled="disabled"
        class="flex items-center justify-center w-7 h-7 rounded text-white/80 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        @mousedown.prevent="redo"
      >
        <span class="i-lucide-redo-2 w-3.5 h-3.5" />
      </button>

      <div class="flex-1" />

      <!-- Char count hint -->
      <span class="text-xs text-white/60 pr-1">{{ charCount }} car.</span>
    </div>

    <!-- Textarea -->
    <textarea
      ref="textareaRef"
      v-model="value"
      :placeholder="placeholder"
      :disabled="disabled"
      rows="5"
      class="w-full bg-transparent px-3 py-2.5 text-sm text-n-slate-12 placeholder-n-slate-9 focus:outline-none resize-y leading-relaxed"
    />

    <!-- Footer error -->
    <div v-if="message && hasError" class="px-3 pb-2 pt-1">
      <p class="text-xs text-n-red-9">{{ message }}</p>
    </div>
  </div>
</template>
